/**
 * Pagination Components
 *
 * Pagination: cursor-based "Load More" (kept for existing usage).
 * NumberedPagination: offset-based numbered page navigation for invoice/bill lists.
 */

import { Button, Skeleton } from '@elsapiens/ui'

// ── Cursor-based (Load More) ─────────────────────────────────────

export interface PaginationProps {
  /** Number of items currently loaded */
  loadedCount: number
  /** Total count of items (from API, if available) */
  totalCount?: number
  /** Whether more pages are available */
  hasNextPage: boolean
  /** Whether next page is currently loading */
  isLoading?: boolean
  /** Show shimmer placeholder instead of real content */
  shimmer?: boolean
  /** Callback to load the next page */
  onLoadMore: () => void
}

const edgeToEdgeStyle = {
  marginLeft: 'calc(var(--el-table-spacer-width) * -1)',
  marginRight: 'calc(var(--el-table-spacer-width) * -1)',
  marginBottom: 'calc(var(--el-table-spacer-width) * -1)',
  width: 'calc(100% + var(--el-table-spacer-width) * 2)',
  paddingLeft: 'var(--el-table-spacer-width)',
  paddingRight: 'var(--el-table-spacer-width)',
  paddingTop: 'var(--el-layout-gap-sm)',
  paddingBottom: 'var(--el-layout-gap-sm)',
}

export function Pagination({
  loadedCount,
  totalCount,
  hasNextPage,
  isLoading = false,
  shimmer = false,
  onLoadMore,
}: PaginationProps): JSX.Element | null {
  if (shimmer) {
    return (
      <div
        className="flex items-center justify-between border-t border-border"
        style={edgeToEdgeStyle}
      >
        <Skeleton height="1em" width="120px" />
      </div>
    )
  }

  if (loadedCount === 0 && !hasNextPage) {return null}

  return (
    <div
      className="flex items-center justify-between border-t border-border"
      style={edgeToEdgeStyle}
    >
      <span className="text-sm text-foreground-secondary">
        Showing {loadedCount}
        {(totalCount ?? 0) > 0 ? ` of ${totalCount}` : ''} items
      </span>
      {hasNextPage && (
        <Button
          disabled={isLoading}
          size="sm"
          variant="secondary"
          onClick={onLoadMore}
        >
          {isLoading ? 'Loading...' : 'Load More'}
        </Button>
      )}
    </div>
  )
}

// ── Numbered (offset-based) ──────────────────────────────────────

export interface NumberedPaginationProps {
  page: number
  totalPages: number
  totalCount: number
  isLoading?: boolean
  onPageChange: (page: number) => void
}

function buildPageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }
  const pages: (number | '...')[] = [1]
  if (current > 3) { pages.push('...') }
  const start = Math.max(2, current - 1)
  const end = Math.min(total - 1, current + 1)
  for (let i = start; i <= end; i++) { pages.push(i) }
  if (current < total - 2) { pages.push('...') }
  pages.push(total)
  return pages
}

export function NumberedPagination({
  page,
  totalPages,
  totalCount,
  isLoading = false,
  onPageChange,
}: NumberedPaginationProps): JSX.Element | null {
  if (totalCount === 0) { return null }

  const pages = buildPageNumbers(page, totalPages)

  return (
    <div
      className="flex items-center justify-between border-t border-border"
      style={edgeToEdgeStyle}
    >
      <span className="text-sm text-foreground-secondary">
        {totalCount} {totalCount === 1 ? 'item' : 'items'}
      </span>
      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          <Button
            disabled={page <= 1 || isLoading}
            size="sm"
            variant="ghost"
            onClick={() => { onPageChange(page - 1) }}
          >
            ‹
          </Button>
          {pages.map((p, i) =>
            p === '...' ? (
              // eslint-disable-next-line react/no-array-index-key -- ellipsis has no stable key
              <span key={`ellipsis-${i}`} className="px-1 text-sm text-foreground-secondary">…</span>
            ) : (
              <Button
                key={p}
                disabled={isLoading}
                size="sm"
                variant={p === page ? 'secondary' : 'ghost'}
                onClick={() => { onPageChange(p as number) }}
              >
                {p}
              </Button>
            ),
          )}
          <Button
            disabled={page >= totalPages || isLoading}
            size="sm"
            variant="ghost"
            onClick={() => { onPageChange(page + 1) }}
          >
            ›
          </Button>
        </div>
      )}
    </div>
  )
}
